import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ShoppingBag, 
  Clock, 
  DollarSign, 
  Headphones, 
  Package, 
  Globe,
  Facebook,
  Instagram,
  MessageCircle,
  Phone,
  Mail,
  Check,
  Menu,
  X,
  Briefcase,
  Zap,
  Info,
  Tag,
  FileText
} from 'lucide-react';
import QuoteForm from '../components/QuoteForm';
import StoreLogos from '../components/StoreLogos';
import WhyChooseUs from '../components/WhyChooseUs';
import TrendingProducts from '../components/TrendingProducts';
import TrendingPicks from '../components/TrendingPicks';

const Home = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const translations = {
    en: {
      hero: {
        title: "Shop From Any USA Store",
        subtitle: "We Purchase, Ship & Deliver to Belize",
        cta: "Get a Quote",
        delivery: "3 Weeks Delivery"
      },
      services: {
        title: "How It Works",
        step1: "Browse & Choose",
        step1desc: "Select products from any top USA online store",
        step2: "We Purchase",
        step2desc: "We buy and ship to our warehouse",
        step3: "We Deliver",
        step3desc: "Delivered to your door in Belize within 3 weeks"
      },
      stores: {
        title: "Stores We Work With",
        subtitle: "And many more top USA online retailers!"
      },
      why: {
        title: "Why Choose Athena's?",
        store: "Any USA Store",
        storedesc: "Access to thousands of products",
        fast: "Fast & Reliable",
        fastdesc: "Consistent 3-week delivery",
        pricing: "Transparent Pricing",
        pricingdesc: "No hidden fees",
        support: "Expert Support",
        supportdesc: "7 days a week assistance"
      },
      contact: {
        title: "Contact Us",
        hours: "Business Hours: 10 AM - 9 PM (7 Days)",
        phone: "Phone",
        email: "Email",
        whatsapp: "WhatsApp Us"
      },
      form: {
        title: "Request a Quote",
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        productUrl: "Product URL",
        description: "Product Description",
        quantity: "Quantity",
        address: "Delivery Address in Belize",
        submit: "Submit Request",
        success: "Quote request submitted! We'll contact you soon."
      }
    },
    es: {
      hero: {
        title: "Compra en Cualquier Tienda de EE.UU.",
        subtitle: "Compramos, Enviamos y Entregamos a Belice",
        cta: "Solicitar Cotización",
        delivery: "Entrega en 3 Semanas"
      },
      services: {
        title: "Cómo Funciona",
        step1: "Navega y Elige",
        step1desc: "Selecciona productos de cualquier tienda en línea de EE.UU.",
        step2: "Nosotros Compramos",
        step2desc: "Compramos y enviamos a nuestro almacén",
        step3: "Entregamos",
        step3desc: "Entregado a tu puerta en Belice en 3 semanas"
      },
      stores: {
        title: "Tiendas con las que Trabajamos",
        subtitle: "¡Y muchas más tiendas en línea de EE.UU.!"
      },
      why: {
        title: "¿Por Qué Elegir a Athena's?",
        store: "Cualquier Tienda de EE.UU.",
        storedesc: "Acceso a miles de productos",
        fast: "Rápido y Confiable",
        fastdesc: "Entrega constante en 3 semanas",
        pricing: "Precios Transparentes",
        pricingdesc: "Sin tarifas ocultas",
        support: "Soporte Experto",
        supportdesc: "Asistencia los 7 días de la semana"
      },
      contact: {
        title: "Contáctanos",
        hours: "Horario: 10 AM - 9 PM (7 Días)",
        phone: "Teléfono",
        email: "Correo",
        whatsapp: "WhatsApp"
      },
      form: {
        title: "Solicitar Cotización",
        name: "Nombre Completo",
        email: "Correo Electrónico",
        phone: "Número de Teléfono",
        productUrl: "URL del Producto",
        description: "Descripción del Producto",
        quantity: "Cantidad",
        address: "Dirección de Entrega en Belice",
        submit: "Enviar Solicitud",
        success: "¡Solicitud enviada! Te contactaremos pronto."
      }
    }
  };

  const t = translations[language];

  const scrollToQuote = () => {
    document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/5016735717`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f7f5f2] to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
              alt="Athena's Online Shopping Services"
              className="h-16 w-auto object-contain cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => navigate('/about')}
              className="text-[#1e1919] hover:text-[#8b1538] font-semibold transition-colors"
            >
              {language === 'en' ? 'About' : 'Acerca de'}
            </button>
            <button 
              onClick={() => navigate('/partner')}
              className="text-[#1e1919] hover:text-[#8b1538] font-semibold transition-colors flex items-center gap-1"
            >
              <span className="text-[#ff8c19]">🤝</span>
              {language === 'en' ? 'Partner' : 'Socios'}
            </button>
            <button 
              onClick={() => navigate('/express-shipping')}
              className="text-[#1e1919] hover:text-[#8b1538] font-semibold transition-colors flex items-center gap-1"
            >
              <span className="text-[#fad24b]">⚡</span>
              {language === 'en' ? 'Express' : 'Exprés'}
            </button>
            <button 
              onClick={() => document.getElementById('trending')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-[#1e1919] hover:text-[#8b1538] font-semibold transition-colors"
            >
              {language === 'en' ? 'Products' : 'Productos'}
            </button>
            <button 
              onClick={() => navigate('/policy')}
              className="text-[#1e1919] hover:text-[#8b1538] font-semibold transition-colors"
            >
              {language === 'en' ? 'Policies' : 'Políticas'}
            </button>
            <button 
              onClick={scrollToQuote}
              className="text-[#1e1919] hover:text-[#8b1538] font-semibold transition-colors"
            >
              {language === 'en' ? 'Get Quote' : 'Cotización'}
            </button>
          </nav>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'es' : 'en')}
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? 'ES' : 'EN'}
            </Button>
            
            {/* Desktop Social Icons */}
            <a 
              href="https://www.facebook.com/profile.php?id=61584846392424" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:block text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a 
              href="https://www.instagram.com/athenasonlineshoppingservices/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:block text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
            
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#736c64]/20">
            <nav className="container mx-auto px-4 py-4 space-y-2">
              <button 
                onClick={() => { navigate('/about'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-[#1e1919] hover:bg-[#8b1538]/10 rounded-lg font-semibold flex items-center gap-3"
              >
                <Info className="w-5 h-5 text-[#8b1538]" />
                {language === 'en' ? 'About' : 'Acerca de'}
              </button>
              
              <button 
                onClick={() => { navigate('/partner'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-[#1e1919] hover:bg-[#ff8c19]/10 rounded-lg font-semibold flex items-center gap-3"
              >
                <Briefcase className="w-5 h-5 text-[#ff8c19]" />
                {language === 'en' ? 'Partner With Us' : 'Asóciate'}
              </button>
              
              <button 
                onClick={() => { navigate('/express-shipping'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-[#1e1919] hover:bg-[#fad24b]/10 rounded-lg font-semibold flex items-center gap-3"
              >
                <Zap className="w-5 h-5 text-[#fad24b]" />
                {language === 'en' ? 'Express Shipping' : 'Envío Exprés'}
              </button>
              
              <button 
                onClick={() => { document.getElementById('trending')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-[#1e1919] hover:bg-[#3dd3ee]/10 rounded-lg font-semibold flex items-center gap-3"
              >
                <Tag className="w-5 h-5 text-[#3dd3ee]" />
                {language === 'en' ? 'Products' : 'Productos'}
              </button>
              
              <button 
                onClick={() => { navigate('/policy'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-[#1e1919] hover:bg-[#736c64]/10 rounded-lg font-semibold flex items-center gap-3"
              >
                <FileText className="w-5 h-5 text-[#736c64]" />
                {language === 'en' ? 'Policies' : 'Políticas'}
              </button>
              
              <button 
                onClick={() => { scrollToQuote(); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 bg-[#8b1538] text-white hover:bg-[#6b0f2a] rounded-lg font-semibold flex items-center gap-3"
              >
                <ShoppingBag className="w-5 h-5" />
                {language === 'en' ? 'Get Quote' : 'Solicitar Cotización'}
              </button>
              
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-[#736c64]/20 mt-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=61584846392424" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
                >
                  <Facebook className="w-7 h-7" />
                </a>
                <a 
                  href="https://www.instagram.com/athenasonlineshoppingservices/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#8b1538] hover:text-[#6b0f2a] transition-colors"
                >
                  <Instagram className="w-7 h-7" />
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-[#fad24b]/20 rounded-full">
            <span className="text-[#8b1538] font-semibold flex items-center gap-2 justify-center">
              <Clock className="w-4 h-4" />
              {t.hero.delivery}
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-[#1e1919] mb-6 leading-tight">
            {t.hero.title}
          </h1>
          
          <p className="text-xl md:text-2xl text-[#736c64] mb-10 max-w-3xl mx-auto">
            {t.hero.subtitle}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              onClick={scrollToQuote}
              className="bg-[#8b1538] hover:bg-[#6b0f2a] text-white px-8 py-6 text-lg rounded-lg transition-all transform hover:scale-105"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              {t.hero.cta}
            </Button>
            
            <Button 
              size="lg" 
              variant="outline"
              onClick={openWhatsApp}
              className="border-[#8b1538] text-[#8b1538] hover:bg-[#8b1538]/10 px-8 py-6 text-lg rounded-lg transition-all"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              {t.contact.whatsapp}
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1e1919] mb-16">
            {t.services.title}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-2 border-[#fad24b]/30 hover:border-[#fad24b] transition-all transform hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#fad24b]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-[#8b1538]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1e1919] mb-3">{t.services.step1}</h3>
                <p className="text-[#736c64]">{t.services.step1desc}</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-[#ff8c19]/30 hover:border-[#ff8c19] transition-all transform hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#ff8c19]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-8 h-8 text-[#8b1538]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1e1919] mb-3">{t.services.step2}</h3>
                <p className="text-[#736c64]">{t.services.step2desc}</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-[#3dd3ee]/30 hover:border-[#3dd3ee] transition-all transform hover:scale-105">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#3dd3ee]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-[#8b1538]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1e1919] mb-3">{t.services.step3}</h3>
                <p className="text-[#736c64]">{t.services.step3desc}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* This Week's Trending Picks */}
      <TrendingPicks language={language} onGetQuote={scrollToQuote} />

      {/* Store Logos */}
      <StoreLogos language={language} />

      {/* Trending Products */}
      <div id="trending"></div>
      <TrendingProducts language={language} />

      {/* Why Choose Us */}
      <WhyChooseUs language={language} translations={t.why} />

      {/* Quote Form */}
      <section id="quote-form" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-3xl">
          <QuoteForm language={language} translations={t.form} />
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 px-4 bg-[#8b1538] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">{t.contact.title}</h2>
          <p className="text-lg mb-8 text-[#fad24b]">{t.contact.hours}</p>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <a 
              href="tel:+5016735717"
              className="flex flex-col items-center gap-2 p-6 bg-white/10 rounded-lg hover:bg-white/20 transition-all"
            >
              <Phone className="w-8 h-8" />
              <span className="font-semibold">{t.contact.phone}</span>
              <span className="text-sm">(501) 673-5717</span>
            </a>
            
            <a 
              href="mailto:athenasonlineshoppingservices@gmail.com"
              className="flex flex-col items-center gap-2 p-6 bg-white/10 rounded-lg hover:bg-white/20 transition-all"
            >
              <Mail className="w-8 h-8" />
              <span className="font-semibold">{t.contact.email}</span>
              <span className="text-sm break-all">athenasonlineshoppingservices@gmail.com</span>
            </a>
            
            <button 
              onClick={openWhatsApp}
              className="flex flex-col items-center gap-2 p-6 bg-white/10 rounded-lg hover:bg-white/20 transition-all"
            >
              <MessageCircle className="w-8 h-8" />
              <span className="font-semibold">{t.contact.whatsapp}</span>
              <span className="text-sm">Chat with us</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e1919] text-white py-8 px-4">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_57e2dd96-4e50-451b-8abf-042890e7a51b/artifacts/m648krxt_Athena%27s%20Logo.png"
              alt="Athena's Online Shopping Services"
              className="h-24 w-auto object-contain"
            />
          </div>
          <div className="flex items-center justify-center gap-6 mb-4">
            <a 
              href="https://www.facebook.com/profile.php?id=61584846392424" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a 
              href="https://www.instagram.com/athenasonlineshoppingservices/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#fad24b] hover:text-white transition-colors"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
          <p className="text-[#bbb5ae] text-sm mb-3">
            Business Hours: 10 AM - 9 PM (7 Days)
          </p>
          <div className="mb-4 text-sm space-y-1">
            <p className="text-[#bbb5ae]">
              Phone: <a href="tel:+5016735717" data-testid="footer-phone-link" className="text-[#fad24b] hover:text-white transition-colors">6735717</a>
            </p>
            <p className="text-[#bbb5ae]">
              WhatsApp: <a href="https://wa.me/5016735717" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-[#fad24b] hover:text-white transition-colors">Chat with us</a>
            </p>
            <p className="text-[#bbb5ae]">
              Email: <a href="mailto:athenasonlineshoppingservices@gmail.com" data-testid="footer-email-link" className="text-[#fad24b] hover:text-white transition-colors break-all">athenasonlineshoppingservices@gmail.com</a>
            </p>
            <p className="text-[#bbb5ae]">
              <a href="/privacy" data-testid="footer-privacy-link" className="text-[#fad24b] hover:text-white transition-colors">Privacy Policy</a>
            </p>
          </div>
          <p className="text-[#bbb5ae] text-sm">
            © {new Date().getFullYear()} Athena's Online Shopping Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
